/**
 * Codec wire framing for peer-to-peer streams.
 *
 * Bandwidth context: a Trystero data channel over BitTorrent / IPFS /
 * Nostr / MQTT relays has very limited sustained throughput — often
 * <100 KB/s per peer for the discovery-and-bootstrap relays. A
 * 500-token LLM completion in JSON-SSE is ~75 KB; in Codec msgpack
 * is ~5 KB; in Codec msgpack + dict-zstd is ~500 B. Codec on the
 * wire is the difference between a usable mesh and a constantly-
 * stalling one.
 *
 * Three send/receive paths:
 *
 *  1. **Chat metadata** (`cm`) — JSON, small. Human typed a message.
 *     Codec doesn't help here; the message is bytes, not tokens.
 *  2. **Streaming completion frames** (`cf`) — binary Codec msgpack
 *     frame per generated chunk. The bandwidth-critical path: a
 *     peer's local LLM streams 500 tokens to another peer as
 *     ~500 bytes of compressed frames instead of ~75 KB of
 *     JSON-SSE text.
 *  3. **Tool calls** (`tc`) — JSON, small.
 *
 * Pass-through property: a receiving peer that wants to FORWARD a
 * Codec frame to a third peer doesn't detokenize. The frame bytes
 * traverse the mesh as-is, only detokenized at the edge that needs
 * UTF-8 for human display.
 */
import { encode as msgpackEncode, decode as msgpackDecode } from '@msgpack/msgpack';
// ── Encode / decode ────────────────────────────────────────────────────────
/**
 * Encode a Codec frame to bytes ready for the Trystero `cf` action.
 * The Uint8Array travels the data channel as a binary message — no
 * base64, no JSON wrapping.
 */
export function encodeFrameBytes(frame) {
    return msgpackEncode(frame);
}
/**
 * Decode incoming `cf` bytes from a peer back into a Codec frame.
 * The bytes are typed as `unknown` at the wire boundary; this fn
 * validates the shape.
 */
export function decodeFrameBytes(bytes) {
    if (!(bytes instanceof Uint8Array))
        return null;
    let raw;
    try {
        raw = msgpackDecode(bytes);
    }
    catch {
        return null;
    }
    if (typeof raw !== 'object' || raw === null)
        return null;
    const r = raw;
    if (!Array.isArray(r.ids))
        return null;
    if (!r.ids.every((n) => typeof n === 'number' && Number.isInteger(n) && n >= 0))
        return null;
    if (typeof r.done !== 'boolean')
        return null;
    return raw;
}
// ── Detokenize at the edge (only when rendering for a human) ───────────────
/**
 * Convenience helper for a receiving peer that wants the frame rendered
 * as UTF-8 for display. Pass a `Detokenizer` instance bound to the
 * sender's tokenizer map (negotiate the map id via the `cap` block's
 * `modelId` + `@codecai/web`'s `discoverMap` / `loadMap`).
 *
 * For agent-to-agent relay paths, DON'T call this — forward the bytes
 * verbatim. That's the whole point.
 */
export function renderFrameForHuman(frame, detok, partial) {
    return detok.render(frame.ids, { partial });
}
export function makeOutboundEncoder(tokenize) {
    return {
        feed(text, opts) {
            const ids = tokenize(text);
            return encodeFrameBytes({
                ids,
                done: opts.done,
                ...(opts.finishReason !== undefined ? { finish_reason: opts.finishReason } : {}),
            });
        },
        reset() {
            // Reserved for future stateful tokenizers; current path is stateless.
        },
    };
}
export function makeInboundAssembler(detok, opts) {
    return {
        push(frame) {
            if (frame.safety && opts.onSafety)
                opts.onSafety(frame.safety);
            if (frame.ids.length > 0) {
                const chunk = detok.render(frame.ids, { partial: !frame.done });
                if (chunk.length > 0)
                    opts.onText(chunk);
            }
            if (frame.done && opts.onDone)
                opts.onDone(frame);
        },
        reset() {
            detok.reset();
        },
    };
}
